// core
import React, { useState } from "react";
import cx from "classnames";
import { PaymentFrequency } from "../../../../../common/interfaces/enums/PaymentFrequency";
import InsurerLogo from "../../../../../common/components/InsurerLogo/InsurerLogo";
import HeaderBreakdown from "../../Components/HeaderBreakdown";
import HeaderCashback from "../../Components/HeaderCashback";
import Premium from "../../../Premium/Premium";
import IQuoteCardResult from "../../../../interfaces/results/IQuoteCardResult";
import { IClient } from "../../../../../common/interfaces/IClient";
import QuoteTableState from "../../../../types/quoteTable/IQuoteTableState";
import styles from "./QuoteTableFull.module.scss";
import QuoteTableOffersAndBenefitsContent from "../Cells/QuoteTableOffersAndBenefitsContent/QuoteTableOffersAndBenefitsContent";
import StarRating from "../../../../../common/components/StarRating/StarRating";
import FinancialStrengthModal from "../../../Modals/FinancialStrengthModal/FinancialStrengthModal";
import QuoteTableFullCardRatings from "./Components/QuoteTableFullCardRatings";
import DocumentLink from "../../../../../common/components/Document/DocumentLink";
import QuoteCallToAction from "../../Components/QuoteCallToAction/QuoteCallToAction";
import QuoteTableFullCardFeature from "./Components/QuoteTableFullCardFeature";
import useCart from "../../../../../common/hooks/useCart";
import IQuoteCardProduct from "../../../../interfaces/results/IQuoteCardProduct";
import ProductType from "../../../../../common/interfaces/enums/ProductType";
import QuoteResultError from "../../../Modals/QuoteResultErrorModal/QuoteResultError";

// interfaces
interface IQuoteTableHeaderContentProps {
    groupId: string;
    bannerHeadingText: string;
    card: IQuoteCardResult;
    clients: IClient[];
    handlePaymentFrequencyChange: (newPaymentFrequency: PaymentFrequency) => void;
    handleSelectProduct: () => void;
    handleClickCustomerServiceRating: () => void;
    quoteState: QuoteTableState;
    className?: string;
}

// rendering
const QuoteTableFullCard: React.FC<IQuoteTableHeaderContentProps> = (props) => {
    const {
        groupId,
        bannerHeadingText,
        card,
        clients,
        handlePaymentFrequencyChange,
        handleClickCustomerServiceRating,
        quoteState,
        className
    } = props;

    const {
        state: { hasBlockingError },
        totalCashback,
        result: { totalPremium, paymentFrequency, providerId, providerName }
    } = card;

    const [isOpen, setIsOpen] = useState(false);

    const [isOpenFinancialStrengthModal, setIsOpenFinancialStrengthModal] = useState(
        false
    );

    const { generateApplicationURL } = useCart();
    return (
        <div
            className={cx([
                styles["basic-panel"],
                "layout-flex-column",
                "text-feature",
                {
                    [styles["is-highlighted"]]: quoteState.isHighlighted,
                    [styles["is-selected"]]: quoteState.isSelected,
                    [styles["has-error"]]: hasBlockingError,
                    [className]: !!className
                }
            ])}>
            <div
                className={cx([
                    "layout-column-banner",
                    "panel-best-choice",
                    "text-banner",
                    {
                        "text-banner-hidden": !bannerHeadingText,
                        "layout-hidden-mobile": !quoteState.isHighlighted
                    }
                ])}>
                {bannerHeadingText}
            </div>

            <Premium
                totalPremium={totalPremium}
                size={"extra-large"}
                paymentFrequency={paymentFrequency}
                isBelowMinimumPremium={!quoteState.isOverMinimumPremium}
                onPaymentFrequencyChange={handlePaymentFrequencyChange}
                useFullFrequencyLabel
                hasBlockingError={hasBlockingError}
                className="gap-m"
            />

            {!hasBlockingError && (
                <React.Fragment>
                    <HeaderBreakdown
                        card={card}
                        clients={clients}
                        hasDiscountApplied={card.result.benefits.some(
                            (b) => b.discounts.length > 0
                        )}
                        className="gap-m"
                    />

                    <HeaderCashback totalCashback={totalCashback} />
                </React.Fragment>
            )}

            <InsurerLogo
                includesAboutInsurerModal
                providerId={providerId}
                className={cx(["layout-insurer-logo"])}
            />

            <div className={cx(["layout-flex-s", "gap-ml", "gap-pre-xl"])}>
                {hasBlockingError ? (
                    <React.Fragment>
                        <QuoteResultError
                            isOpen={isOpen}
                            onClose={() => setIsOpen(false)}
                            errors={card.result.errors}
                        />
                        <span
                            className={cx([
                                "text-feature",
                                { [className]: !!className }
                            ])}>
                            Insurer not available.
                            <a onClick={() => setIsOpen(true)}> Find out why.</a>
                        </span>
                    </React.Fragment>
                ) : (
                    <QuoteTableOffersAndBenefitsContent
                        hasInlineTitle
                        quoteState={quoteState}
                        cashbackAmount={card.totalCashback}
                        providerAddons={card.providerAddons}
                        providerId={card.result.providerId}
                        providerName={card.result.providerName}
                        specialOffers={card.specialOffers.map((s) => s.specialOfferName)}
                    />
                )}
            </div>

            {/* Financial Strength */}
            <FinancialStrengthModal
                providerName={providerName}
                providerId={providerId}
                providerRating={card.ratings.financialStrength.ratingInfo}
                isOpen={isOpenFinancialStrengthModal}
                onClose={() => setIsOpenFinancialStrengthModal(false)}
            />
            <QuoteTableFullCardRatings
                title="Financial strength"
                onClickLearnMore={() => setIsOpenFinancialStrengthModal(true)}
                className="gap-m">
                <span className="text-large">
                    {card.ratings.financialStrength.ratingInfo.rating}
                </span>
                <span>{card.ratings.financialStrength.ratingInfo.ratingAgency}</span>
            </QuoteTableFullCardRatings>

            <QuoteTableFullCardRatings
                title="Customer service"
                onClickLearnMore={handleClickCustomerServiceRating}
                className="gap-m">
                <StarRating
                    rating={card.ratings.customerService.ratingInfo}
                    maxRating={5}
                    alignment="end"
                />
            </QuoteTableFullCardRatings>

            {/* Policy Features */}

            {Object.keys(card.productInfo).map((productType: ProductType) => {
                const productInfo: IQuoteCardProduct = card.productInfo[productType];

                const policyFeatures = [...productInfo.policyFeatures].sort(
                    (a, b) => a.orderBy - b.orderBy
                );

                return (
                    <React.Fragment key={productType}>
                        <h3 className={cx("text-policy-name")}>
                            {productInfo.productName}
                        </h3>
                        <div className="gap-m">
                            {policyFeatures.map((feature) => {
                                return (
                                    <QuoteTableFullCardFeature
                                        key={feature.sectionId}
                                        feature={feature}
                                        className="gap-ml"
                                    />
                                );
                            })}
                        </div>

                        <h3 className="text-font-size-ml gap-pre-none gap-m">
                            Documents
                        </h3>

                        <DocumentLink
                            title="View product brochure"
                            documentUrl={productInfo.documents.brochure.documentUrl}
                            thumbnailUrl={productInfo.documents.brochure.thumbnailUrl}
                            className="gap-m"
                        />

                        <DocumentLink
                            title="View policy document"
                            documentUrl={productInfo.documents.policy.documentUrl}
                            thumbnailUrl={productInfo.documents.policy.thumbnailUrl}
                            className="gap-l"
                        />
                    </React.Fragment>
                );
            })}

            <QuoteCallToAction
                groupId={groupId}
                card={card}
                    isDisabled={
                        hasBlockingError ||
                        !quoteState.isOverMinimumPremium ||
                        quoteState.isDisabled
                    }
                    url={generateApplicationURL(groupId, card)}
                    className="btn-small"
                    premium={totalPremium}
                />
        </div>
    );
};

export default React.memo(QuoteTableFullCard);
